import {
  Avatar,
  DeleteIcon,
  Divider,
  GKButton,
  GKDatePicker,
  GKSearchSelect,
  GKTextField,
  Grid2,
  IconButton,
  SimpleLoader,
  styled,
  Typography,
} from "components";
import dayjs from "dayjs";
import { ProfileAPI } from "hooks/ProfileAPI";
import { useFetchQuery } from "hooks/useQueries";
import { isEmpty } from "lodash";
import EditLogoModal from "Pages/CompanyInformation/EditLogoModal";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  SELECTED_CITY,
  SELECTED_COUNTRY,
  SELECTED_STATE,
} from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { USER_PROFILE_DETAILS_BY_ID } from "utils/apiRoutes";
import { PERSONAL_GENDER } from "utils/constants";
import useDocumentTitle from "utils/useDocumentTitle";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const UserProfile = () => {
  useDocumentTitle("User Profile");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);

  const userData = useAppSelector((state) => state?.user?.userData);
  const countryList = useAppSelector((state) => state?.app?.countryList);
  const stateList = useAppSelector((state) => state?.app?.stateList);
  const cityList = useAppSelector((state) => state?.app?.cityList);

  const dispatch = useAppDispatch();

  const { handleUpdateProfile, updateProfileUpdateLoading } = ProfileAPI();

  const { data: profileData, isLoading } = useFetchQuery({
    route: USER_PROFILE_DETAILS_BY_ID(userData?.id),
    key: ["GET_PROFILE_DETAILS"],
    enabled: Boolean(userData?.id),
    success: (response: any) => {
      if (!isEmpty(response?.data?.data?.country)) {
        dispatch(
          SELECTED_COUNTRY(
            countryList.find(
              (dataItem) => dataItem.name === response?.data?.data?.country,
            )?.id,
          ),
        );
      }

      if (!isEmpty(response?.data?.data?.state)) {
        dispatch(
          SELECTED_STATE(
            stateList.find(
              (dataItem) => dataItem.name === response?.data?.data?.state,
            )?.id,
          ),
        );
      }

      if (!isEmpty(response?.data?.data?.city)) {
        dispatch(
          SELECTED_CITY(
            cityList.find(
              (dataItem) => dataItem.name === response?.data?.data?.city,
            )?.id,
          ),
        );
      }
    },
  });

  const form = useForm({
    defaultValues: {
      profilePic: [],
      email: "",
      name: "",
      gender: "",
      maritalStatus: "",
      fatherName: "",
      motherName: "",
      dateOfBirth: "",
      address: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      occupation: "",
      pan: "",
      aadhar: "",
      emergencyContactCountryCode: "+91",
      emergencyContact: "",
      emergencyContactNum: "",
    },
    values: {
      ...profileData?.data?.data,
      profilePic: [],
      phone: `${userData?.countryCode}-${userData?.phone}`,
      email: userData?.email,
    },
  });

  if (isLoading) return <SimpleLoader />;

  const onSubmit = (values: any) => {
    const payload = {
      email: values?.email,
      name: values?.name,
      gender: values?.gender,
      maritalStatus: values?.maritalStatus,
      fatherName: values?.fatherName,
      motherName: values?.motherName,
      dateOfBirth: values?.dateOfBirth,
      address: values?.address,
      city: values?.city,
      state: values?.state,
      country: values?.country,
      pincode: values?.pincode,
      occupation: values?.occupation,
      pan: values?.pan,
      aadhar: values?.aadhar,
      emergencyContact: values?.emergencyContact,
      emergencyContactNum: values?.emergencyContactNum,
      emergencyContactCountryCode: values?.emergencyContactCountryCode,
    };

    handleUpdateProfile(payload);
  };

  const handleProfilePicChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
        setIsModalOpen(true);
      };
      reader.readAsDataURL(file);
      event.target.value = "";
    }
  };

  const handleProfilePicDelete = () => {
    const formData = new FormData();
    formData.append("profilePic", "");
    handleUpdateProfile(formData);
    setCroppedImage(null);
  };

  const handleCropComplete = (croppedImageUrl: string) => {
    setCroppedImage(croppedImageUrl);
    setIsModalOpen(false);

    fetch(croppedImageUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File(
          [blob],
          `profile-picture.${blob.type.split("/")[1]}`,
          {
            type: blob?.type,
          },
        );
        form.setValue("profilePic", [file]);

        const formData = new FormData();
        formData.append("profilePic", file);
        handleUpdateProfile(formData);
      });
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} id="profile-form">
      <Grid2 container spacing={2}>
        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Personal info
          </Typography>
        </Grid2>
        <Grid2 container size={9} spacing={2}>
          <Grid2
            container
            size={12}
            display={"flex"}
            alignItems={"center"}
            gap={1}
          >
            <Grid2>
              <Avatar
                alt=""
                src={croppedImage || userData?.profilePic}
                sx={{ width: 72, height: 72, border: "1px solid #eee" }}
              />
            </Grid2>
            <Grid2 ml={1}>
              <GKButton
                variant="contained"
                component="label"
                role={undefined}
                tabIndex={-1}
              >
                Upload Profile Photo{" "}
                <VisuallyHiddenInput
                  type="file"
                  accept={".jpg, .png, .svg"}
                  onChange={handleProfilePicChange}
                />
              </GKButton>
            </Grid2>
            <Grid2>
              <IconButton
                sx={{ border: "1px solid #CDD5DF", borderRadius: "8px", p: 1 }}
                onClick={handleProfilePicDelete}
              >
                <DeleteIcon width={18} />
              </IconButton>
            </Grid2>
          </Grid2>

          <Grid2 size={4}>
            <Controller
              name="name"
              control={form.control}
              // rules={{
              //   required: {
              //     value: true,
              //     message: "Please enter a valid name",
              //   },
              //   validate: validateNotWhitespaceOnly,
              // }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKTextField
                    {...field}
                    inputLabel="Name"
                    // requiredField
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="phone"
              control={form.control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKTextField
                    {...field}
                    inputLabel="Phone"
                    disabled
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKTextField
                    {...field}
                    inputLabel="Email"
                    // requiredField
                    disabled
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="aadhar"
              control={form.control}
              render={({ field }) => (
                <GKTextField {...field} inputLabel="Aadhar Number" />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="dateOfBirth"
              control={form.control}
              // rules={{
              //   required: {
              //     value: true,
              //     message: "Please Select Birth of Date",
              //   },
              // }}
              render={({ field, fieldState: { error } }) => (
                <GKDatePicker
                  {...field}
                  // requiredField
                  inputLabel="Date Of Birth"
                  value={dayjs(field.value)}
                  onChange={(value) => {
                    field.onChange(dayjs(value).format("YYYY-MM-DD"));
                  }}
                  minDate={dayjs("01-01-1901")}
                  slotProps={{
                    textField: {
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="gender"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  disableClearable={false}
                  inputLabel="Gender"
                  options={PERSONAL_GENDER || []}
                  value={
                    PERSONAL_GENDER.find(
                      (dataItem) => dataItem.value === field?.value,
                    ) || ""
                  }
                  onChange={(event, val) => field.onChange(val?.value)}
                  getOptionLabel={(option) => option.name || option}
                  error={!!error}
                  helperText={!!error && error.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="occupation"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Occupation"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="fatherName"
              control={form.control}
              render={({ field }) => (
                <GKTextField {...field} inputLabel="Father's name" />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="motherName"
              control={form.control}
              render={({ field }) => (
                <GKTextField {...field} inputLabel="Mother's name" />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Address Details
          </Typography>
        </Grid2>
        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="address"
              control={form.control}
              // rules={{
              //   required: {
              //     value: true,
              //     message: "Please Enter Address",
              //   },
              //   validate: validateNotWhitespaceOnly,
              // }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Address"
                  // requiredField
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={3}>
            <Controller
              name="pincode"
              control={form.control}
              // rules={{
              //   required: {
              //     value: true,
              //     message: "Please Enter Pincode",
              //   },
              // }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  type="number"
                  inputLabel="Pincode"
                  // requiredField
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={3}>
            <Controller
              name="country"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="Country"
                  value={
                    countryList.find(
                      (dataItem) => dataItem.name === field?.value,
                    ) || ""
                  }
                  options={countryList || []}
                  getOptionLabel={(e) => e.name || e}
                  onChange={(e, val) => {
                    field.onChange(val.name);
                    form.setValue("city", "");
                    form.setValue("state", "");
                    dispatch(SELECTED_COUNTRY(val.id));
                  }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={3}>
            <Controller
              name="state"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="State"
                  value={
                    stateList.find(
                      (dataItem) => dataItem.name === field?.value,
                    ) || ""
                  }
                  options={stateList || []}
                  getOptionLabel={(e) => e.name || e}
                  onChange={(e, val) => {
                    field.onChange(val.name);
                    form.setValue("city", "");
                    dispatch(SELECTED_STATE(val.id));
                  }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={3}>
            <Controller
              name="city"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="City"
                  error={!!error}
                  helperText={error?.message}
                  value={
                    cityList.find(
                      (dataItem) => dataItem.name === field?.value,
                    ) || ""
                  }
                  options={cityList || []}
                  getOptionLabel={(e) => e.name || e}
                  onChange={(e, val) => {
                    field.onChange(val.name);
                    dispatch(SELECTED_CITY(val.id));
                  }}
                />
              )}
            />
          </Grid2>
        </Grid2>

        {/* <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Secondary Contact Details
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={4}>
            <Controller
              name="emergencyContact"
              control={form.control}
              render={({ field }) => (
                <GKTextField {...field} inputLabel="Contact Name" />
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="emergencyContactNum"
              control={form.control}
              render={({ field }) => (
                <PhoneInput
                  {...field}
                  onSelectChange={(val: any) => {
                    form.setValue("emergencyContactCountryCode", val);
                  }}
                  onTextChange={(event: any) => {
                    field.onChange(event);
                  }}
                  onTextValue={field.value}
                  onSelectValue={form.getValues("emergencyContactCountryCode")}
                  hasError={hasError}
                  setHasError={setHasError}
                />
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="email"
              control={form.control}
              render={({ field }) => (
                <GKTextField {...field} inputLabel="Contact Email" />
              )}
            />
          </Grid2>
        </Grid2>
*/}
        <Grid2 size={12} textAlign={"right"} my={1}>
          <GKButton
            form="profile-form"
            variant="contained"
            type="submit"
            loading={updateProfileUpdateLoading}
          >
            Save
          </GKButton>
        </Grid2>

        {isModalOpen && (
          <EditLogoModal
            open={isModalOpen}
            setOpen={setIsModalOpen}
            img={selectedImage}
            onCropComplete={handleCropComplete}
          />
        )}
      </Grid2>
    </form>
  );
};

export default UserProfile;
